import { addDays, differenceInCalendarDays, format, parseISO, startOfWeek } from 'date-fns';
import { TZDate } from '@date-fns/tz';

/**
 * כל חישוב תאריך במערכת עובר כאן.
 * אזור הזמן מגיע מ app_settings.timezone, עם ברירת מחדל שאינה ספציפית ללקוח.
 * שבוע מתחיל ביום ראשון (PRD 2.2).
 */
export const DEFAULT_TIMEZONE = 'Asia/Jerusalem';

let timezone: string = DEFAULT_TIMEZONE;

/** נקרא פעם אחת בעליית האפליקציה, מתוך app_settings. */
export function setTimezone(tz: string) {
  timezone = tz || DEFAULT_TIMEZONE;
}

export function getTimezone() {
  return timezone;
}

/** התאריך והשעה "עכשיו" באזור הזמן של המערכת. */
export function now(): TZDate {
  return TZDate.tz(timezone);
}

function toZoned(value: Date | string): TZDate {
  const date = typeof value === 'string' ? parseISO(value) : value;
  return new TZDate(date, timezone);
}

/** 08.10.2026 */
export function formatDate(value: Date | string): string {
  return format(toZoned(value), 'dd.MM.yyyy');
}

/** 09:00 */
export function formatTime(value: Date | string): string {
  return format(toZoned(value), 'HH:mm');
}

/** 08.10.2026, 21:14 */
export function formatDateTime(value: Date | string): string {
  return format(toZoned(value), 'dd.MM.yyyy, HH:mm');
}

/**
 * ראשון, שני ... לפי האינדקס של `getDay()`.
 *
 * זו עובדת לוח שנה ולא רשימה עסקית, ולכן אינה טבלה (PRD 2.4): אין רשת
 * שבה השבוע מתחיל ביום אחר. מיוצא כדי שלא ייווצרו עוד עותקים במסכים.
 */
export const WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'] as const;

export function weekdayName(value: Date | string): string {
  return WEEKDAYS[toZoned(value).getDay()];
}

/** תחילת השבוע (יום ראשון) של התאריך הנתון. */
export function weekStart(value: Date | string = now()): TZDate {
  return new TZDate(startOfWeek(toZoned(value), { weekStartsOn: 0 }), timezone);
}

/** שבעת ימי השבוע שמתחיל בתאריך הנתון. */
export function weekDays(start: Date | string): TZDate[] {
  const first = weekStart(start);
  return Array.from({ length: 7 }, (_, i) => new TZDate(addDays(first, i), timezone));
}

/** yyyy-MM-dd, הפורמט שבו תאריכים נשמרים ב Postgres. */
export function toDateKey(value: Date | string): string {
  return format(toZoned(value), 'yyyy-MM-dd');
}

/**
 * המועד הבא שנופל ביום ובשעה הנתונים, קדימה בלבד.
 *
 * משמש לברירת המחדל של תזמון הפרסום. "חמישי ב 20:00" ביום חמישי
 * ב 21:00 הוא חמישי הבא, ולא לפני שעה: ברירת מחדל שנמצאת בעבר
 * אינה ברירת מחדל אלא שגיאה שמחכה ללחיצה.
 */
export function nextWeekdayAt(
  weekday: number,
  hour: number,
  minute = 0,
  from: Date | string = now(),
): TZDate {
  const base = toZoned(from);
  const days = (weekday - base.getDay() + 7) % 7;
  const at = new TZDate(addDays(base, days), timezone);
  at.setHours(hour, minute, 0, 0);
  return at.getTime() > base.getTime() ? at : new TZDate(addDays(at, 7), timezone);
}

/**
 * הערך של <input type="datetime-local">, באזור הזמן של המערכת.
 *
 * השדה הזה חסר אזור זמן לחלוטין, ולכן הוא חייב לקבל ולהחזיר שעה
 * של הרשת. בלי זה מנהל שנמצא בחו"ל היה מתזמן לשעה אחרת ממה שראה.
 */
export function toLocalInputValue(value: Date | string): string {
  return format(toZoned(value), "yyyy-MM-dd'T'HH:mm");
}

/** ההפך: מה שהוקלד בשדה, כרגע אמיתי בציר הזמן. null אם אינו תקין. */
export function fromLocalInputValue(text: string): TZDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(text);
  if (!match) return null;

  const [, year, month, day, hour, minute] = match.map(Number);
  const at = new TZDate(year, month - 1, day, hour, minute, 0, 0, timezone);
  return Number.isNaN(at.getTime()) ? null : at;
}

/** מספר הימים שנשארו עד תאריך. שלילי אם עבר. */
export function daysUntil(value: Date | string): number {
  return differenceInCalendarDays(toZoned(value), now());
}

/**
 * "נשארו 6 ימים", ומתחת ל 48 שעות "נשארו 20 שעות" (PRD 5.4, מסך האילוצים).
 * השעות מעוגלות כלפי מעלה והימים נספרים כימי לוח, כדי שדדליין שנמצא
 * בדיוק בעוד שישה ימים לא יוצג כחמישה בגלל כמה אלפיות שנייה.
 */
export function timeLeftLabel(deadline: Date | string): string {
  const target = toZoned(deadline);
  const diffMs = target.getTime() - now().getTime();
  if (diffMs <= 0) return 'המועד עבר';

  const hours = Math.ceil(diffMs / 3_600_000);
  if (hours <= 48) {
    return hours <= 1 ? 'נשארה שעה' : `נשארו ${hours} שעות`;
  }

  const days = Math.max(1, daysUntil(target));
  return days === 1 ? 'נשאר יום' : `נשארו ${days} ימים`;
}
